/**
 * The apothecary's hearth (lifted from lantern-water's `hearth.js` and the
 * hearth furniture of `world.js` `createShop`): a genuine arched opening
 * through the masonry backed by a sooted wall, a jointed front arch with a
 * proud keystone, ashlar jambs, three charred logs with their end rings,
 * glowing coals, and the animated pixel flame (a 48 × 64 quantised shader on
 * the shared environment clock, `kit.uniforms.breezeTime`); then the mantel
 * shelf, the hearth stone, iron fire dogs, the mantel moulding, an old teapot
 * and the fire's halo. Origin: the hearth's centre on the floor (the
 * prototype's (5.45, 0.1)), its opening toward +z. No params. The fire's
 * point light and the collider are the scene's.
 */
import * as THREE from 'three';
import type { Kit } from '../kit.ts';
import type { Prefab } from './types.ts';

/** Planar stone UVs at the masonry's 2.5 m repeat (`hearth.js` `stoneUV`). */
const stoneUV = <G extends THREE.BufferGeometry>(geometry: G): G => {
  const p = geometry.getAttribute('position');
  const n = geometry.getAttribute('normal');
  const uv = geometry.getAttribute('uv');
  for (let i = 0; i < uv.count; i += 1) {
    const nx = Math.abs(n.getX(i));
    const ny = Math.abs(n.getY(i));
    uv.setXY(i, (nx > 0.5 ? p.getZ(i) : p.getX(i)) / 2.5, (ny > 0.5 ? p.getZ(i) : p.getY(i)) / 2.5);
  }
  return geometry;
};

const finishes = (kit: Kit): { readonly soot: THREE.Material; readonly coal: THREE.Material; readonly fire: THREE.Material } => ({
  soot: kit.derive('hearth:soot', kit.m.stone, (soot) => {
    soot.color.set('#403732');
    soot.bumpScale = 0.025;
  }),
  coal: kit.shared('hearth:coal', () => new THREE.MeshStandardMaterial({ color: '#30201a', emissive: '#d9470c', emissiveIntensity: 0.7, roughness: 1 })),
  fire: kit.shared(
    'hearth:fire',
    () =>
      new THREE.ShaderMaterial({
        uniforms: { time: kit.uniforms.breezeTime },
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        forceSinglePass: true,
        vertexShader: 'varying vec2 vUv;void main(){vUv=uv;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}',
        fragmentShader: `varying vec2 vUv;uniform float time;
float tongue(vec2 uv,float x,float width,float height,float phase,float t){
  height+=sin(t*2.6+phase)*.07+sin(t*5.3+phase*1.7)*.025;
  float y=uv.y/max(height,.1);
  float curl=sin(y*5.-t*3.1+phase)*.045*y;
  float w=width*pow(max(0.,1.-y),.72);
  return (1.-smoothstep(w*.45,w+.014,abs(uv.x-x+curl)))*(1.-smoothstep(.91,1.,y));
}
void main(){
  vec2 uv=(floor(vUv*vec2(48.,64.))+.5)/vec2(48.,64.);
  float t=floor(time*12.)/12.;
  float fire=max(max(tongue(uv,.23,.15,.56,1.,t),tongue(uv,.39,.14,.82,3.,t)),max(tongue(uv,.56,.16,.68,5.,t),tongue(uv,.73,.14,.58,7.,t)));
  fire*=smoothstep(0.,.06,uv.y);
  if(fire<.04)discard;
  vec3 color=mix(vec3(1.25,.075,.008),vec3(2.05,.54,.025),smoothstep(.18,.85,fire));
  float core=smoothstep(.77,1.,fire)*(1.-smoothstep(.12,.42,uv.y));
  color=mix(color,vec3(2.25,1.42,.32),core);
  gl_FragColor=vec4(color,fire*.95);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`,
      }),
  ),
});

export const prefab: Prefab = {
  id: 'hearth',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    root.name = 'recessed-shop-hearth';
    const { kit, m } = ctx;
    const { soot, coal, fire } = finishes(kit);
    const block = (material: THREE.Material, x: number, y: number, z: number, w: number, h: number, d: number): THREE.Mesh => ctx.mesh(root, stoneUV(new THREE.BoxGeometry(w, h, d)), material, x, y, z);
    const outline = new THREE.Shape();
    outline.moveTo(-0.9, 0);
    outline.lineTo(0.9, 0);
    outline.lineTo(0.9, 2.3);
    outline.lineTo(-0.9, 2.3);
    outline.closePath();
    const opening = new THREE.Path();
    opening.moveTo(-0.6, 0.2);
    opening.lineTo(0.6, 0.2);
    opening.lineTo(0.6, 1.4);
    opening.absarc(0, 1.4, 0.6, 0, Math.PI, false);
    opening.lineTo(-0.6, 0.2);
    opening.closePath();
    outline.holes.push(opening);
    ctx.mesh(root, stoneUV(new THREE.ExtrudeGeometry(outline, { depth: 0.9, bevelEnabled: false, steps: 1, curveSegments: 12 })), m.stone, 0, 0, -0.45).name = 'hearth-masonry-opening';
    block(soot, 0, 1.12, -0.51, 1.32, 1.9, 0.1).name = 'hearth-sooted-back';
    block(soot, 0, 0.19, 0, 1.19, 0.04, 0.86);
    // Jointed front arch and narrow ashlar jambs.
    for (let i = 0; i < 11; i += 1) {
      const a = (i * Math.PI) / 11 + 0.012;
      const b = ((i + 1) * Math.PI) / 11 - 0.012;
      const wedge = new THREE.Shape();
      wedge.moveTo(0.6 * Math.cos(a), 0.6 * Math.sin(a));
      wedge.lineTo(0.77 * Math.cos(a), 0.77 * Math.sin(a));
      wedge.absarc(0, 0, 0.77, a, b, false);
      wedge.lineTo(0.6 * Math.cos(b), 0.6 * Math.sin(b));
      wedge.absarc(0, 0, 0.6, b, a, true);
      wedge.closePath();
      ctx.mesh(root, stoneUV(new THREE.ExtrudeGeometry(wedge, { depth: i === 5 ? 0.14 : 0.1, bevelEnabled: false, curveSegments: 2, steps: 1 })), i === 5 ? m.cream : m.stone, 0, 1.4, 0.465);
    }
    for (const side of [-1, 1]) {
      for (let i = 0; i < 5; i += 1) block(m.stone, side * 0.685, 0.32 + i * 0.24, 0.52, 0.17, 0.227, 0.13);
      block(m.cream, side * 0.685, 1.39, 0.53, 0.23, 0.08, 0.17);
    }
    // Stacked charred logs sit inside the opening rather than on its front face.
    const logGeometry = kit.shared('hearth:log', () => new THREE.CylinderGeometry(0.095, 0.11, 0.93, 10, 1));
    const ringGeometry = kit.shared('hearth:log-ring', () => new THREE.TorusGeometry(0.061, 0.007, 4, 12));
    [-0.16, 0.1, 0.33].forEach((z, i) => {
      const log = ctx.mesh(root, logGeometry, m.darkWood, (i - 1) * 0.06, 0.32 + (i === 1 ? 0.09 : 0), z);
      log.rotation.set(0, (i - 1) * 0.18, Math.PI / 2);
      for (const side of [-1, 1]) ctx.mesh(log, ringGeometry, m.wood, 0, side * 0.469, 0).rotation.x = Math.PI / 2;
    });
    const coalGeometry = kit.shared('hearth:coal-shape', () => new THREE.IcosahedronGeometry(0.06, 0));
    for (let i = 0; i < 18; i += 1) {
      const ember = ctx.mesh(root, coalGeometry, coal, ((i % 6) - 2.5) * 0.16, 0.235 + Math.sin(i * 2.4) * 0.015, Math.floor(i / 6) * 0.19 - 0.17);
      ember.scale.set(1.2 + (i % 3) * 0.17, 0.5, 1);
    }
    const flame = ctx.mesh(root, kit.shared('hearth:flame', () => new THREE.PlaneGeometry(1.05, 1.36)), fire, 0, 0.92, 0.29);
    flame.castShadow = false;
    flame.name = 'hearth-flames';
    // The mantel, hearth stone, fire dogs, moulding and the teapot (`world.js` `createShop`).
    ctx.box(root, 0, 2.34, 0, 2.2, 0.18, 1.22, m.wood);
    ctx.box(root, 0, 0.1, 0.4, 2.2, 0.18, 1.6, m.stone);
    for (const x of [-0.47, 0.47]) {
      ctx.box(root, x, 0.42, 0.87, 0.045, 0.48, 0.045, m.iron);
      ctx.mesh(root, ctx.sphere, m.brass, x, 0.68, 0.87, 0.065, 0.065, 0.065);
      ctx.line(root, [x, 0.25, 0.88], [x, 0.25, 0.42], 0.055, m.iron);
    }
    ctx.box(root, 0, 2.19, 0.53, 1.94, 0.12, 0.25, m.cream);
    for (const x of [-0.87, 0.87]) ctx.box(root, x, 1.2, 0.54, 0.15, 2, 0.17, m.cream);
    ctx.mesh(root, ctx.sphere, m.teal, 0.15, 2.66, 0.06, 0.27, 0.22, 0.23);
    ctx.cylinder(root, 0.15, 2.87, 0.06, 0.18, 0.065, m.brass);
    ctx.mesh(root, ctx.sphere, m.brass, 0.15, 2.94, 0.06, 0.045, 0.055, 0.045);
    ctx.mesh(root, kit.shared('hearth:teapot-handle', () => new THREE.TorusGeometry(0.17, 0.03, 6, 12)), m.teal, 0.44, 2.7, 0.06);
    ctx.line(root, [-0.08, 2.65, 0.06], [-0.28, 2.83, 0.06], 0.07, m.teal);
    ctx.glow(root, 0, 0.72, 0.9, 2.2, 0.18);
    return { root, sockets: { light: [0, 1.2, 1.2] } };
  },
};
